import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { PositionCatalog } from "@/services/backend/htz/position-catalog/types/position-catalog";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import {
  rtkErrIsValidationError,
  ValidationError,
} from "@/shared/lib/errors/validation-error";
import { Confirmation } from "@/services/backend/htz/confirmations/types/confirmation";
import { useHtzConfirmationPutMutation } from "@/services/backend/htz/confirmations/api/confirmation-api";
import { Order } from "@/services/backend/htz/orders/types/order";
import { Offer } from "@/services/backend/htz/offers/types/offer";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";
import { AirHandlingUnit } from "@/services/backend/htz/ahu/types/air-handling-unit";

interface ConfirmationContextInterface {
  confirmation: Confirmation;
  onConfirmationChange: (confirmation: Confirmation) => void;
  validationError: ValidationError | null;
  setValidationError: (err: ValidationError | null) => void;
  airHandlingUnits: AirHandlingUnit[];
  positionCatalog: PositionCatalog;
  orders: Order[];
  offers: Offer[];
}

export const ConfirmationContext = createContext<ConfirmationContextInterface>({
  confirmation: {} as Confirmation,
  onConfirmationChange: () => null,
  validationError: null,
  setValidationError: () => null,
  positionCatalog: {} as PositionCatalog,
  airHandlingUnits: [],
  orders: [],
  offers: [],
});

export function useConfirmationContext() {
  return useContext(ConfirmationContext);
}

export function ConfirmationContextProvider({
  children,
  confirmation: propConfirmation,
  catalog: propCatalog,
  airHandlingUnits: propAirHandlingUnits,
  offers: propOffers,
  orders: propsOrders,
}: {
  children: ReactNode;
  confirmation: Confirmation;
  catalog: PositionCatalog;
  airHandlingUnits: AirHandlingUnit[];
  offers: Offer[];
  orders: Order[];
}) {
  // idea would be to use index db here for offline availability
  const [confirmation, setConfirmation] = useState<Confirmation>();
  const [catalog, setCatalog] = useState<PositionCatalog>();
  const [airHandlingUnits, setAirHandlingUnits] = useState<AirHandlingUnit[]>();
  const [offers, setOffers] = useState<Offer[]>();
  const [orders, setOrders] = useState<Order[]>();
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);

  // after vector clocks are added, they can be used to
  // update the confirmation here.
  if (!confirmation || confirmation.id !== propConfirmation.id) {
    setConfirmation(propConfirmation);
  }

  // This updates the UI if one of the references
  // was changed, e.g. after a copy operation.
  useRefEffect(propConfirmation.offerId, () => {
    setConfirmation(propConfirmation);
  });
  useRefEffect(propConfirmation.orderId, () => {
    setConfirmation(propConfirmation);
  });
  // Once the confirmation is festgeschrieben on the server, force a resync so
  // the local buffer reflects the new immutability state and the freshly
  // assigned document number — local edits are no longer possible past this
  // point.
  useRefEffect(propConfirmation.immutable, () => {
    setConfirmation(propConfirmation);
  });
  // The data card writes the fields below through the patch endpoint, i.e.
  // past this buffer. Resync when one of them changes on the server, so the
  // next put of the buffer does not revert the patch.
  useRefEffect(
    [
      propConfirmation.date,
      propConfirmation.orderDate,
      propConfirmation.orderReference,
      propConfirmation.offerDate,
      propConfirmation.offerReference,
      propConfirmation.specialAgreements,
      propConfirmation.internalComment,
    ].join("|"),
    () => {
      setConfirmation(propConfirmation);
    },
  );

  // Neither, the catalog nor the air handling units are mutated within the
  // confirmation context. Any value coming is must be an updated server value.
  if (!catalog || catalog !== propCatalog) {
    setCatalog(propCatalog);
  }
  if (!airHandlingUnits || airHandlingUnits !== propAirHandlingUnits) {
    setAirHandlingUnits(propAirHandlingUnits);
  }
  if (!offers || offers !== propOffers) {
    setOffers(propOffers);
  }
  if (!orders || orders !== propsOrders) {
    setOrders(propsOrders);
  }

  const onConfirmationChange = (changedConfirmation: Confirmation) => {
    setConfirmation(changedConfirmation);
  };

  const value = useMemo(
    () => ({
      confirmation: confirmation!,
      onConfirmationChange,
      validationError,
      setValidationError,
      airHandlingUnits: airHandlingUnits!,
      positionCatalog: catalog!,
      offers: offers!,
      orders: orders!,
    }),
    [airHandlingUnits, catalog, confirmation, validationError, orders, offers],
  );

  return (
    <ConfirmationContext.Provider value={value}>
      <ConfirmationUpdater>{children}</ConfirmationUpdater>
    </ConfirmationContext.Provider>
  );
}

function ConfirmationUpdater({ children }: PropsWithChildren) {
  // this should only run when online
  const { confirmation, onConfirmationChange, setValidationError } =
    useConfirmationContext();
  const mutation = useHtzConfirmationPutMutation();
  const [, { error }] = mutation;

  useEffect(() => {
    if (rtkErrIsValidationError(error)) {
      setValidationError(error.data);
    }
  }, [error, setValidationError]);

  // The confirmation context buffers the confirmation locally and surfaces
  // validation errors inline via setValidationError, so we keep the user's
  // input on error rather than resetting it (resetOnError: false). The true
  // server value is not available here; resetState mirrors the buffer.
  useDebouncedMutation(
    confirmation,
    (value) =>
      onConfirmationChange(
        value instanceof Function ? value(confirmation) : value,
      ),
    mutation,
    confirmation,
    { skip: confirmation.immutable, resetOnError: false },
  );

  return children;
}
